import { computed, watch, type ComputedRef } from 'vue'
import { useRouter } from 'vue-router'

import { healthCheckApi } from '../api/HealthCheckApi'
import { LoadHealthChecksFlow } from '../flows/LoadHealthChecksFlow'
import { RunFindingActionFlow } from '../flows/RunFindingActionFlow'
import { TriageFindingFlow } from '../flows/TriageFindingFlow'
import { VerifyCheckpointFlow } from '../flows/VerifyCheckpointFlow'
import { SUMMARY_CATEGORIES } from '../models/healthCheck.constants'
import type {
  HealthCheckCategory,
  HealthCheckCounts,
  HealthCheckFinding,
  HealthCheckGroups,
  HealthCheckTriageAction,
} from '../models/healthCheck.types'
import { useHealthCheckStore } from '../stores/healthCheckStore'

import { useAccount, useWorkspace } from '@/features/account'
import { useMetaCheckpoint } from '@/features/integrations'
import type { ApiError } from '@/shared/http'
import { useI18n } from '@/shared/i18n'
import { useToast, type UiAsyncState } from '@/shared/ui'

interface LoadFlow {
  execute: () => Promise<void>
}

interface TriageFlow {
  execute: (finding: HealthCheckFinding, action: HealthCheckTriageAction) => Promise<void>
}

interface ActionFlow {
  execute: (finding: HealthCheckFinding) => Promise<void>
}

interface HealthCheckFlows {
  load: LoadFlow
  triage: TriageFlow
  action: ActionFlow
  /** The Meta checkpoint card's verify (ORP-26183); absent, its action runs like any other. */
  checkpoint?: ActionFlow
  openFacebook?: () => void
}

/** Internal injection seam: production callers use the no-argument contract. */
export interface UseHealthChecksOptions {
  flows?: HealthCheckFlows
}

export interface UseHealthChecks {
  state: ComputedRef<UiAsyncState>
  error: ComputedRef<ApiError | null>
  findings: ComputedRef<HealthCheckFinding[]>
  grouped: ComputedRef<HealthCheckGroups>
  counts: ComputedRef<HealthCheckCounts>
  summaryCategories: typeof SUMMARY_CATEGORIES
  expanded: ComputedRef<Readonly<Record<HealthCheckCategory, boolean>>>
  selected: ComputedRef<HealthCheckFinding | null>
  isDrawerOpen: ComputedRef<boolean>
  triagePending: ComputedRef<Readonly<Record<number, boolean>>>
  actionPending: ComputedRef<Readonly<Record<number, boolean>>>
  checkpointNotices: ComputedRef<Readonly<Record<number, string>>>
  openFacebook: () => void
  isExpanded: (category: HealthCheckCategory) => boolean
  canExpand: (category: HealthCheckCategory) => boolean
  toggle: (category: HealthCheckCategory) => void
  visible: (category: HealthCheckCategory) => HealthCheckFinding[]
  select: (finding: HealthCheckFinding) => void
  closeDrawer: () => void
  load: () => Promise<void>
  reload: () => Promise<void>
  dismiss: (finding: HealthCheckFinding) => Promise<void>
  restore: (finding: HealthCheckFinding) => Promise<void>
  runAction: (finding: HealthCheckFinding) => Promise<void>
}

export function useHealthChecks(options: UseHealthChecksOptions = {}): UseHealthChecks {
  const account = useAccount()
  const workspace = useWorkspace()
  const store = useHealthCheckStore()
  const flows = options.flows ?? createDefaultFlows()

  const load = (): Promise<void> => flows.load.execute()
  const triage = (finding: HealthCheckFinding, action: HealthCheckTriageAction): Promise<void> =>
    flows.triage.execute(finding, action)
  const runAction = (finding: HealthCheckFinding): Promise<void> =>
    finding.isMetaCheckpoint && flows.checkpoint
      ? flows.checkpoint.execute(finding)
      : flows.action.execute(finding)

  watch(
    () => [account.accountId.value, workspace.workspaceId.value] as const,
    () => {
      store.resetState()
      void load()
    },
    { immediate: true },
  )

  return {
    state: computed(() => store.status),
    error: computed(() => store.error),
    findings: computed(() => store.findings ?? []),
    grouped: computed(() => store.grouped),
    counts: computed(() => store.counts),
    summaryCategories: SUMMARY_CATEGORIES,
    expanded: computed(() => store.expanded),
    selected: computed(() => store.selected),
    isDrawerOpen: computed(() => store.isDrawerOpen),
    triagePending: computed(() => store.triagePending),
    actionPending: computed(() => store.actionPending),
    checkpointNotices: computed(() => store.checkpointNotices),
    openFacebook: () => flows.openFacebook?.(),
    isExpanded: (category) => store.expanded[category],
    canExpand: (category) => store.canExpand(category),
    toggle: (category) => store.toggleExpanded(category),
    visible: (category) => store.visible(category),
    select: (finding) => store.select(finding.id),
    closeDrawer: () => store.clearSelection(),
    load,
    reload: load,
    dismiss: (finding) => triage(finding, 'dismiss'),
    restore: (finding) => triage(finding, 'restore'),
    runAction,
  }

  function createDefaultFlows(): HealthCheckFlows {
    const router = useRouter()
    const toast = useToast()
    const { t } = useI18n()
    const getters = {
      accountId: () => account.accountId.value,
      workspaceId: () => numericId(workspace.workspaceId.value),
    }
    const clock = { now: Date.now }
    const notifier = {
      error: (messageKey: string, params?: Record<string, unknown>) =>
        toast.error(params ? t(messageKey, params) : t(messageKey)),
      success: (messageKey: string, params?: Record<string, unknown>) =>
        toast.success(params ? t(messageKey, params) : t(messageKey)),
    }
    const metaCheckpoint = useMetaCheckpoint()
    const loadFlow = new LoadHealthChecksFlow({
      store,
      api: healthCheckApi,
      getters,
      clock,
      include: (finding) => !finding.isMetaCheckpoint || metaCheckpoint.canSee.value,
    })
    const triageFlow = new TriageFindingFlow({
      store,
      api: healthCheckApi,
      getters,
      clock,
      notifier,
      reload: () => loadFlow.execute(),
    })
    const actionFlow = new RunFindingActionFlow({
      store,
      api: healthCheckApi,
      notifier,
      openExternal: (target) => {
        window.open(target, '_blank', 'noopener,noreferrer')
      },
      navigateInternal: async (target) => {
        await router.push(target)
      },
      reload: () => loadFlow.execute(),
    })
    const checkpointFlow = new VerifyCheckpointFlow({
      store,
      verify: (path) => metaCheckpoint.verify(path),
      translate: (key) => t(key),
      notifier,
      reload: () => loadFlow.execute(),
    })
    return {
      load: loadFlow,
      triage: triageFlow,
      action: actionFlow,
      checkpoint: checkpointFlow,
      openFacebook: metaCheckpoint.openFacebook,
    }
  }
}

function numericId(value: string | number | undefined): number | undefined {
  if (value === undefined || (typeof value === 'string' && value.trim() === '')) return undefined
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : undefined
}
